import PropTypes from 'prop-types';

import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import AsyncTableComponent from '../../Table/AsyncTableContainer';
import CommunityLinkContainer from '../Link/CommunityLinkContainer';

import { toSentenceCase } from '../../../utils';
import { getExportTitle } from '../../ViewExport/utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../../Table/constants';

import { TABLE_DATA_TYPES } from '../../../staticData';
import { ActionButton, ButtonLink } from '../../Button';
import StyledLink from '../../StyledLink';
import {
  // approvalScopeFilter,
  limitFilter,
  pmcFilter,
} from '../../Table/filters';
import ApprovalsDropDown from './ApprovalsDropDown';
import CommunitySetupExport from './CommunitySetupExport';

const StyledIconButton = styled(IconButton)(() => ({
  position: 'absolute',
  zIndex: 1,
  opacity: 0,
  transition: 'all 0.2s ease-in-out 0.2s',
}));

const StyledNameContainer = styled(Box)(() => ({
  display: 'flex',
  position: 'relative',
  height: '4rem',
  '&:hover': {
    '& > button': {
      opacity: 1,
    },
    '& > div': {
      marginLeft: '3rem',
    },
  },
}));

const StyledNameLink = styled(StyledLink)(() => ({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
}));

const StyledBuildingContainer = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  border: `1px solid ${theme.palette.custom.primary2}`,
  borderRadius: '14px',
  color: theme.palette.custom.primary2,
  display: 'flex',
  margin: '2px 1rem 2px 0',
  maxWidth: '100%',
  padding: '0 1rem',
  '&:first-of-child': {
    border: 'none',
    padding: 0,
  },
}));

const StyledCommunityName = styled(Box)(() => ({
  zIndex: 2,
  transition: 'all 0.5s ease-out',
  marginLeft: 0,
}));

const CommunitySetupComponent = (props) => {
  const theme = useTheme();

  const {
    onLoad,
    items,
    getItems,
    listParams,
    listKey,
    pmcs,
    onExport,
    onGetCommunityList,
    onCommunityUpdate,
    openModal,
    closeModal,
  } = props;

  const grey = theme.palette.grey[400];

  const getModalProps = (entry = {}, isLinking = false) => {
    const { name, buildings } = entry;

    const communityName = name || buildings?.[0]?.name;
    const title = `${
      isLinking ? `link` : `update`
    } community - ${communityName}`;

    return {
      title,
      maxWidth: 'md',
      closeModal,
      customTitleStyle: {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      },
      render: (props) => (
        <CommunityLinkContainer
          {...props}
          currentCommunity={entry}
          onCommunityUpdate={onCommunityUpdate}
          isLinking={isLinking}
          onGetCommunityList={onGetCommunityList}
          listParams={listParams}
        />
      ),
    };
  };

  const columns = [
    {
      Header: 'Community',
      id: 'community',
      sortable: false,
      minWidth: 200,
      accessor: (entry, isExport) => {
        const { name } = entry;
        if (isExport) return name || '';

        const showUpdateModal = () => openModal(getModalProps(entry));

        return (
          <StyledNameContainer>
            <StyledIconButton size="small" onClick={showUpdateModal}>
              <EditIcon fontSize="small" />
            </StyledIconButton>
            <StyledCommunityName className="crop-long-text">
              {name}
            </StyledCommunityName>
          </StyledNameContainer>
        );
      },
    },
    {
      Header: 'Building(s)',
      id: 'first_building_name',
      minWidth: 200,
      headerClassName: 'sort',
      accessor: (entry, isExport) => {
        const { buildings } = entry;
        if (isExport) return buildings?.map(({ name }) => name).join(', ');
        return (
          <Box display="flex" flexWrap="wrap">
            {buildings?.map(({ id, name }, i) => {
              const onClearClick = () =>
                onCommunityUpdate(entry, {
                  buildings: buildings
                    ?.map(({ id }) => id)
                    ?.filter((id, index) => index !== i),
                });

              return (
                <StyledBuildingContainer key={id}>
                  <StyledNameLink to={`/building/${id}`}>{name}</StyledNameLink>
                  {i !== 0 && (
                    <IconButton
                      onClick={onClearClick}
                      color="inherit"
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </StyledBuildingContainer>
              );
            })}
          </Box>
        );
      },
    },
    {
      Header: 'Community Link',
      id: 'link',
      sortable: false,
      width: 160,
      accessor: (entry) => {
        const { enabled } = entry;

        const showLinkModal = () => openModal(getModalProps(entry, true));

        if (enabled)
          return (
            <ButtonLink onClick={showLinkModal}>Link community</ButtonLink>
          );
        return '-';
      },
    },
    {
      Header: 'Community',
      id: 'enabled',
      headerClassName: 'sort',
      width: 150,
      accessor: (entry, isExport) => {
        const { enabled, ...rest } = entry;
        const label = enabled ? 'On' : 'Off';
        if (isExport) return label;
        return (
          <ActionButton
            variant="contained"
            disableElevation
            style={enabled ? {} : { backgroundColor: grey }}
            onClick={() => onCommunityUpdate(rest, { enabled: !enabled })}
            fullWidth
          >
            {label}
          </ActionButton>
        );
      },
    },
    {
      Header: 'Content Approval',
      id: 'approval_scope',
      headerClassName: 'sort',
      width: 180,
      accessor: (entry, isExport) => {
        const { approvalScope, ...rest } = entry;
        const options = [
          'post',
          'marketplace',
          'recommendation',
          'skip_comment_approve',
        ];

        const onUpdate = (values) =>
          onCommunityUpdate(rest, { approvalScope: values });

        if (isExport)
          return approvalScope?.map((item) => toSentenceCase(item))?.join(', ');

        return (
          <ApprovalsDropDown
            options={options}
            approvalScope={approvalScope}
            onCommunityUpdate={onUpdate}
          />
        );
      },
    },
  ];

  const filters = [
    pmcFilter(listParams, pmcs),
    // TODO: disabled approvalScopeFilter since we don`t need it
    // approvalScopeFilter(listParams),
    limitFilter(listParams),
  ];

  const defaultParams = {
    pmc: null,
    q: null,
    enabled: null,
    approval_scope: null,
    ordering: 'first_building_name',
    new_approval_scope: true,
  };

  const action = null;
  const tableDataType = TABLE_DATA_TYPES.items;

  const title = 'Community Setup';
  const exportTitle = getExportTitle(title);
  const exportActions = (
    <CommunitySetupExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );

  return (
    <AsyncTableComponent
      tableId={TABLE_ID.communitySetup}
      title={title}
      columns={columns}
      defaultSort={TABLE_CONSTANTS.first_building_name}
      filters={filters}
      showFiltersAreApplied={false}
      onLoad={onLoad}
      items={items}
      getItems={getItems}
      listParams={listParams}
      defaultParams={defaultParams}
      tableDataType={tableDataType}
      listKey={listKey}
      action={action}
      manual
      defaultSortDesc
      exportActions={exportActions}
      searchAutoFocus={false}
    />
  );
};

CommunitySetupComponent.propTypes = {
  pmcs: PropTypes.instanceOf(Object).isRequired,
  onLoad: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onGetCommunityList: PropTypes.func.isRequired,
  onCommunityUpdate: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  items: PropTypes.instanceOf(Object).isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  listKey: PropTypes.string.isRequired,
};

export default CommunitySetupComponent;
